import { faBan, faDatabase, faHourglass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Column, DataTable, TableActionButton } from "components/DataTable";
import { useApi } from "../../../../hooks/useApi";
import { useConfirmedCallback } from "hooks/useConfirmedCallback";
import useMediaQuery from "hooks/useMediaQuery";
import moment from "moment";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DatesComponent } from "../BookingList";
import dynamic from "next/dynamic";
import { useModal } from "../../../../hooks/useModal";

const EditBookingForm = dynamic(() => import("../EditBookingForm"));
const FormDataPreview = dynamic(() => import("../../../Formular/FormDataPreview"));

interface Props {
}

export const UserBookingList: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({}: Props) => {
    const api = useApi();
    const { t } = useTranslation("bookings");
    const [setModal, resetModal] = useModal();
    const asyncList = useRef<any>(undefined);
    const isMobile = useMediaQuery("(max-width: 650px)");

    const handleCancelBooking = useConfirmedCallback((bookingId: number) => {
        api({
            url: `booking/${bookingId}/cancel`,
            method: "DELETE",
            onSuccess: () => asyncList.current?.reload()
        });
    });

    const handleShowEditBooking = useCallback((formDataId: number) => {
        setModal({
            title: t("booking.userList.editModalTitle"),
            content: <EditBookingForm cb={resetModal} formDataId={formDataId} />,
            show: true
        });
    }, [t]);

    const handleShowFormDataPreview = useCallback((item: any) => setModal({
        title: t("booking.userList.dataModalTitle"),
        content: <FormDataPreview formDataId={item.form_data_id} />,
        show: true
    }), [t]);

    const columns = useMemo(
        (): Column[] => [
            !isMobile && { name: "#", uid: "id" },
            {
                name: t("booking.userList.columns.event"),
                uid: "event.title",
                renderer: (_, item) => (
                    <>
                        {item.is_waiting && (
                            <FontAwesomeIcon icon={faHourglass} className="tw-mr-2 tw-text-accents-6" />
                        )}
                        {item.event.title}
                    </>
                )
            },
            { name: t("booking.userList.columns.dates"), uid: "start", renderer: (_, item) => <DatesComponent booking={item} /> },
            !isMobile && {
                name: t("booking.userList.columns.bookedAt"),
                uid: "created_at",
                renderer: (date) => moment(date).format("D.M.Y, H:mm")
            },
            { name: t("booking.userList.columns.options"), uid: "actions", align: "end" }
        ],
        [isMobile, t]
    );

    return (
        <DataTable
            asyncListRef={asyncList}
            hideSearchBar
            columns={columns}
            fetchUrl={"user/bookings"}
            tableProps={{ compact: false }}
            rowActions={(item) => (
                <TableActionButton.Container justify="flex-end">
                    {item.form_data_id && <TableActionButton
                        color="primary"
                        icon={faDatabase}
                        tooltip={t("booking.userList.tooltips.showData")}
                        onPress={() => handleShowFormDataPreview(item)}
                    />}
                    {!item.cancelled_at && item.is_cancellable && (
                        <>
                            <TableActionButton
                                color="warning"
                                icon={faBan}
                                tooltip={t("booking.userList.tooltips.editData")}
                                onPress={() => handleShowEditBooking(item.form_data_id)}
                            />
                            <TableActionButton
                                color="error"
                                icon={faBan}
                                tooltip={t("booking.userList.tooltips.cancel")}
                                onPress={() => handleCancelBooking(item.id)}
                            />
                        </>
                    )}
                </TableActionButton.Container>
            )}
        />
    );
};
